import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Building2, LayoutGrid, TrendingUp, Users } from 'lucide-react';
import type { CompanyEngagement, PlatformOverview } from '../../../lib/api';
import type { PlatformTrendPoint } from '../../../lib/api/platformAnalytics';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { SummaryCard } from './SummaryCard';
import { ChartEmptyState } from './ChartEmptyState';
import { ENGAGEMENT_LEVEL_LABEL } from '../platformAnalyticsConfig';

const LEVEL_COLORS: Record<string, string> = {
  strong: '#10b981',
  moderate: '#f59e0b',
  low: '#f97316',
  inactive: '#94a3b8',
};

interface OverviewTabProps {
  overview: PlatformOverview | null;
  trend: PlatformTrendPoint[];
  companies: CompanyEngagement[];
}

export function OverviewTab({ overview, trend, companies }: OverviewTabProps) {
  const { formatDate } = useFormatters();
  const kpis = overview?.kpis;
  const weeklyRegulars = kpis?.stickiness_wau_mau ?? 0;

  const levelCounts = companies.reduce<Record<string, number>>((acc, c) => {
    const key = c.engagement_level || 'inactive';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const engagementPie = Object.entries(levelCounts).map(([level, count]) => ({
    name: ENGAGEMENT_LEVEL_LABEL[level] || level,
    value: count,
    level,
  }));

  const trendData = trend.map((point) => ({
    ...point,
    label: formatDate(point.date, { format: 'DD-MMM-YYYY' }),
  }));

  return (
    <div className="space-y-6">
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <SummaryCard icon={Users} label="Active today" value={kpis.dau} hint="Used the portal today" color="text-blue-600" />
          <SummaryCard icon={Users} label="Active this week" value={kpis.wau} hint="At least once in 7 days" color="text-indigo-600" />
          <SummaryCard icon={Users} label="Active this month" value={kpis.mau} hint="At least once in 30 days" color="text-purple-600" />
          <SummaryCard
            icon={Building2}
            label="Organisations active"
            value={kpis.active_companies}
            hint={`${kpis.active_supplier_companies} sites · ${kpis.active_customer_companies} partners`}
            color="text-emerald-600"
          />
          <SummaryCard
            icon={LayoutGrid}
            label="Weekly regulars"
            value={`${weeklyRegulars}%`}
            hint="Monthly users who returned this week"
            color="text-amber-600"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5" />
              People using the portal over time
            </CardTitle>
            <CardDescription>Daily count of unique people — Login As sessions excluded.</CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <ChartEmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                  <Tooltip
                    formatter={(value: number) => [value, 'People']}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload as PlatformTrendPoint | undefined;
                      return row?.date ? formatDate(row.date) : '';
                    }}
                  />
                  <Area type="monotone" dataKey="unique_users" stroke="#6366f1" fill="url(#usersGradient)" strokeWidth={2} />
                  <Line type="monotone" dataKey="unique_companies" stroke="#10b981" strokeWidth={2} dot={false} name="Organisations" />
                  <Legend />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Organisation engagement mix</CardTitle>
            <CardDescription>How actively each company is using the portal.</CardDescription>
          </CardHeader>
          <CardContent>
            {engagementPie.length === 0 ? (
              <ChartEmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={engagementPie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {engagementPie.map((entry) => (
                      <Cell key={entry.level} fill={LEVEL_COLORS[entry.level] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
